<template>
  <div class="tool-detail-page white-theme pollo-layout">
    <div class="container">
      <section class="first-screen">
      <div class="breadcrumb">
        <router-link to="/">{{ $t('modelDetail.breadcrumb.home') }}</router-link>
        <span>&rsaquo;</span>
        <router-link to="/tools?category=video">{{ $t('tools.page.allTools') }}</router-link>
        <span>&rsaquo;</span>
        <span>{{ toolText('name') }}</span>
      </div>

      <div class="first-screen-grid"><div class="action-column">
      <div v-if="!isLoggedIn" class="login-prompt">
        <div class="login-prompt-content">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <h2>{{ $t('tools.detail.loginRequired') }}</h2>
          <p>{{ $t('tools.detail.loginRequiredDesc') }}</p>
          <button class="btn btn-primary" @click="goLogin">{{ $t('tools.detail.loginNow') }}</button>
        </div>
      </div>

      <div v-else class="tool-interface">
        <div
          class="tool-upload"
          @click="triggerUpload"
          @dragover.prevent
          @drop.prevent="onDrop"
        >
          <input type="file" ref="fileInput" accept="video/*" style="display:none" @change="handleFile">
          <div v-if="!uploaded" class="upload-placeholder">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
            <p>{{ $t('tools.detail.clickUpload') }}{{ $t('tools.fileTypes.video') }}</p>
            <span>{{ $t('tools.detail.support') }} MP4, MOV, WebM</span>
          </div>
          <div v-else class="upload-preview">
            <div class="preview-box preview-box--video">
              <video v-if="previewSrc" :src="previewSrc" class="preview-video" controls playsinline></video>
            </div>
            <button class="btn btn-secondary" @click.stop="clearUpload">{{ $t('tools.detail.reupload') }}</button>
          </div>
        </div>

        <p v-if="processError && !processing" class="tool-error tool-error--block">{{ processError }}</p>

        <div class="tool-options">
          <button class="btn btn-primary btn-lg" :disabled="!uploaded || processing" @click="process">
            <svg v-if="processing" class="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            <span v-else>{{ $t('tools.detail.process') }}</span>
          </button>
        </div>

        <div class="tool-result legacy-tool-result" v-if="processing || processed">
          <div v-if="processing" class="gen-loading">
            <div class="loading-spinner"></div>
            <p>{{ $t('tools.detail.processing') }}</p>
          </div>
          <div v-else class="tool-output">
            <div class="output-comparison">
              <div class="output-before">
                <span class="output-label">{{ $t('tools.detail.before') }}</span>
                <div class="output-video-frame">
                  <video v-if="previewSrc" :src="previewSrc" class="compare-video" controls playsinline></video>
                </div>
              </div>
              <div class="output-after">
                <span class="output-label">{{ $t('tools.detail.after') }}</span>
                <div class="output-video-frame">
                  <video v-if="resultSrc" :src="resultSrc" class="compare-video" controls playsinline></video>
                </div>
              </div>
            </div>
            <div class="output-actions">
              <button v-if="resultSrc" type="button" class="btn btn-primary" @click="downloadResult">⬇️ {{ $t('tools.detail.download') }}</button>
              <button class="btn btn-secondary" @click="reset">{{ $t('tools.detail.processNew') }}</button>
            </div>
          </div>
        </div>
      </div>

      </div><div class="showcase-column">
      <div class="tool-hero"><div class="tool-hero-info"><h1>{{ toolText('name') }}</h1><p class="tool-hero-desc">{{ $t('brandSeo.toolDescription', { description: toolText('fullDesc') }) }}</p></div></div>
      <div v-if="!processing && !processed && hasDemoCompare" class="video-demo-section workspace-panel">
        <h2 class="demo-title">{{ $t('tools.detail.demo') }}</h2>
        <div class="video-compare" :style="{ '--compare-position': comparePercent + '%' }">
          <video
            ref="demoAfterVideo"
            class="video-compare-media"
            :src="demoAfterVideoSrc"
            autoplay
            muted
            loop
            playsinline
          ></video>
          <div class="video-compare-before" :style="compareBeforeStyle">
            <video
              ref="demoBeforeVideo"
              class="video-compare-media"
              :src="demoBeforeVideoSrc"
              autoplay
              muted
              loop
              playsinline
              @loadedmetadata="syncDemoVideos"
              @play="onDemoPlay"
              @pause="onDemoPause"
              @timeupdate="onDemoTimeUpdate"
              @seeking="syncDemoVideos"
            ></video>
          </div>
          <div class="video-compare-handle" aria-hidden="true">
            <span class="video-compare-handle-line"></span>
            <span class="video-compare-handle-knob">
              <span></span>
              <span></span>
            </span>
          </div>
          <span class="video-compare-label video-compare-label-before">{{ $t('tools.detail.before') }}</span>
          <span class="video-compare-label video-compare-label-after">{{ $t('tools.detail.after') }}</span>
          <input
            v-model.number="comparePercent"
            class="video-compare-range"
            type="range"
            min="0"
            max="100"
            step="1"
            aria-label="Video comparison"
          >
        </div>
      </div>

      <div class="processing-panel workspace-panel" v-else-if="processing"><div class="workspace-heading"><h2>{{ $t('tools.detail.processing') }}</h2></div><div class="processing-preview processing-preview--video"><video v-if="previewSrc" :src="previewSrc" autoplay muted loop playsinline></video><div class="processing-overlay"><div class="loading-spinner"></div></div></div><div class="processing-track"><span></span></div></div>
      <div class="result-panel workspace-panel" v-else><div class="workspace-heading"><h2>{{ $t('tools.detail.after') }}</h2></div><div class="output-comparison"><div class="output-before"><span class="output-label">{{ $t('tools.detail.before') }}</span><div class="output-video-frame"><video v-if="previewSrc" :src="previewSrc" class="compare-video" controls playsinline></video></div></div><div class="output-after"><span class="output-label">{{ $t('tools.detail.after') }}</span><div class="output-video-frame"><video v-if="resultSrc" :src="resultSrc" class="compare-video" controls playsinline></video></div></div></div><div class="output-actions"><button v-if="resultSrc" type="button" class="btn btn-primary" @click="downloadResult">{{ $t('tools.detail.download') }}</button><button class="btn btn-secondary" @click="reset">{{ $t('tools.detail.processNew') }}</button></div></div>
      </div></div>
      </section>

      <div class="detail-section" style="margin-top:64px;">
        <h2>{{ $t('tools.detail.howToUse') }}</h2>
        <div class="steps">
          <div class="step" v-for="(step, idx) in steps" :key="idx">
            <div class="step-number">{{ idx + 1 }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h2>{{ $t('tools.detail.moreTools') }}</h2>
        <div class="grid-5">
          <ToolListCard
            v-for="t in otherTools"
            :key="t.id"
            :tool-id="t.id"
            :icon="t.icon"
            :badge="t.badge"
            :to="'/tools/' + t.id"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadFile, saveOrUpdateAIHistory, formatCreateDate, submitCartoonVideoStyleConverter, submitAnimeVideoStyleConverter, submitOilPaintingVideoStyleConverter, submitCyberpunkVideoStyleConverter, pollCapybaraVideoResult } from '@/api'
import ToolListCard from '@/components/ToolListCard.vue'

var MAX_VIDEO_BYTES = 100 * 1024 * 1024
var TOOL_CONFIGS = {
  CartoonVideoStyleConverterDetail: {
    id: 'cartoon-video-style-converter',
    icon: '🎬',
    beforeVideo: function () { return require('@/assets/images/ai_video_cartoon.mp4') },
    afterVideo: function () { return require('@/assets/images/ai_video_cartoon2.mp4') },
    submit: submitCartoonVideoStyleConverter
  },
  AnimeVideoStyleConverterDetail: {
    id: 'anime-video-style-converter',
    icon: '🎞️',
    beforeVideo: function () { return require('@/assets/images/ai_video_anime.mp4') },
    afterVideo: function () { return require('@/assets/images/ai_video_anime2.mp4') },
    submit: submitAnimeVideoStyleConverter
  },
  OilPaintingVideoStyleConverterDetail: {
    id: 'oil-painting-video-style-converter',
    icon: '🖌️',
    beforeVideo: function () { return require('@/assets/images/ai_video_oil_painting.mp4') },
    afterVideo: function () { return require('@/assets/images/ai_video_oil_painting2.mp4') },
    submit: submitOilPaintingVideoStyleConverter
  },
  CyberpunkVideoStyleConverterDetail: {
    id: 'cyberpunk-video-style-converter',
    icon: '🌃',
    beforeVideo: function () { return require('@/assets/images/ai_video_cyberpunk.mp4') },
    afterVideo: function () { return require('@/assets/images/ai_video_cyberpunk2.mp4') },
    submit: submitCyberpunkVideoStyleConverter
  }
}

export default {
  name: 'VideoStyleConverterDetail',
  components: { ToolListCard: ToolListCard },
  data() {
    return {
      uploaded: false,
      processing: false,
      processed: false,
      previewSrc: '',
      resultSrc: '',
      uploadedFile: null,
      processError: null,
      poller: null,
      comparePercent: 50,
      demoSyncing: false
    }
  },
  beforeDestroy() {
    if (this.poller && typeof this.poller.stop === 'function') this.poller.stop()
    this.revokePreview()
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.userInfo
    },
    toolConfig() {
      return TOOL_CONFIGS[this.$route.name] || TOOL_CONFIGS.CartoonVideoStyleConverterDetail
    },
    toolId() {
      return this.toolConfig.id
    },
    toolIcon() {
      return this.toolConfig.icon
    },
    hasDemoCompare() {
      return !!(this.toolConfig && this.toolConfig.beforeVideo && this.toolConfig.afterVideo)
    },
    demoBeforeVideoSrc() {
      return this.hasDemoCompare ? this.toolConfig.beforeVideo() : ''
    },
    demoAfterVideoSrc() {
      return this.hasDemoCompare ? this.toolConfig.afterVideo() : ''
    },
    compareBeforeStyle() {
      var right = 100 - Number(this.comparePercent || 0)
      return { clipPath: 'inset(0 ' + right + '% 0 0)' }
    },
    otherTools() {
      var currentToolId = this.toolId
      return this.$store.state.tools.filter(function(t) {
        return t.category === 'video' && t.id !== currentToolId
      }).slice(0, 5)
    },
    steps() {
      return [
        { title: this.$t('tools.steps.upload.title'), desc: this.$t('tools.steps.upload.desc') },
        { title: this.$t('tools.steps.process.title'), desc: this.$t('tools.steps.process.desc') },
        { title: this.$t('tools.steps.download.title'), desc: this.$t('tools.steps.download.desc') }
      ]
    }
  },
  methods: {
    goLogin() {
      this.$store.dispatch('showLoginModal')
    },
    getToolText(id, field) {
      var tools = this.$t('tools')
      if (tools && typeof tools === 'object' && tools[id] && tools[id][field]) {
        return tools[id][field]
      }
      return this.$t('tools.' + id + '.' + field)
    },
    toolText(field) {
      return this.getToolText(this.toolId, field)
    },
    getDemoVideos() {
      return {
        before: this.$refs.demoBeforeVideo,
        after: this.$refs.demoAfterVideo
      }
    },
    syncDemoVideos() {
      var videos = this.getDemoVideos()
      var before = videos.before
      var after = videos.after
      if (!before || !after || this.demoSyncing) return
      this.demoSyncing = true
      if (Number.isFinite(before.currentTime) && Math.abs(after.currentTime - before.currentTime) > 0.08) {
        after.currentTime = before.currentTime
      }
      if (!before.paused && after.paused) {
        after.play().catch(function () {})
      }
      this.demoSyncing = false
    },
    onDemoPlay() {
      var videos = this.getDemoVideos()
      if (videos.after) videos.after.play().catch(function () {})
    },
    onDemoPause() {
      var videos = this.getDemoVideos()
      if (videos.after) videos.after.pause()
    },
    onDemoTimeUpdate() {
      this.syncDemoVideos()
    },
    triggerUpload() {
      if (this.$refs.fileInput) this.$refs.fileInput.click()
    },
    onDrop(e) {
      var files = e.dataTransfer && e.dataTransfer.files
      if (files && files[0]) this.readFile(files[0])
    },
    handleFile(e) {
      var input = e.target
      var file = input.files && input.files[0]
      if (!file) {
        if (input) input.value = ''
        return
      }
      this.readFile(file)
      if (input) input.value = ''
    },
    readFile(file) {
      this.processError = null
      if (!file.type || file.type.indexOf('video/') !== 0) {
        this.processError = this.toolText('notVideoFile')
        return
      }
      if (file.size > MAX_VIDEO_BYTES) {
        this.processError = this.toolText('fileTooLarge')
        return
      }
      this.revokePreview()
      this.previewSrc = URL.createObjectURL(file)
      this.uploadedFile = file
      this.uploaded = true
      this.processed = false
      this.resultSrc = ''
    },
    revokePreview() {
      if (this.previewSrc && this.previewSrc.indexOf('blob:') === 0) {
        URL.revokeObjectURL(this.previewSrc)
      }
    },
    clearUpload() {
      this.revokePreview()
      this.uploaded = false
      this.previewSrc = ''
      this.uploadedFile = null
      this.resultSrc = ''
      this.processed = false
      this.processing = false
      this.processError = null
      if (this.poller && typeof this.poller.stop === 'function') this.poller.stop()
      this.poller = null
    },
    reset() {
      this.clearUpload()
    },
    downloadResult() {
      if (!this.resultSrc) return
      var a = document.createElement('a')
      a.href = this.resultSrc
      a.download = this.toolText('downloadName')
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    process() {
      var self = this
      if (!this.uploadedFile) return
      this.processing = true
      this.processed = false
      this.processError = null

      var createDate = formatCreateDate(new Date())
      var toolId = this.toolId
      var aiName = this.getToolText(toolId, 'name')
      var aiDesc = this.getToolText(toolId, 'desc')
      var historyId = null
      var originalUrl = ''
      var promptId = ''

      uploadFile(this.uploadedFile).then(function(url) {
        originalUrl = url
        return saveOrUpdateAIHistory({
          aiName: aiName,
          taskId: '',
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'video',
          createDate: createDate
        })
      }).then(function(hRes) {
        historyId = hRes && (hRes.historyId != null ? hRes.historyId : null)
        return self.toolConfig.submit(originalUrl)
      }).then(function(submitRes) {
        promptId = submitRes && submitRes.promptId ? submitRes.promptId : ''
        if (!promptId) return Promise.reject(new Error(toolId + ' did not return prompt_id'))
        saveOrUpdateAIHistory({
          aiName: aiName,
          historyId: historyId,
          taskId: promptId,
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'video',
          createDate: createDate
        }).catch(function() {})
        self.poller = pollCapybaraVideoResult(promptId, function(err, result) {
          if (result && result.status === 'processing') return
          self.poller = null
          if (err) {
            self.onProcessFailed(err, historyId, promptId, aiName, aiDesc, originalUrl, createDate)
            return
          }
          var resultUrl = result && result.url ? result.url : ''
          self.processing = false
          self.processed = true
          self.resultSrc = resultUrl
          self.processError = null
          saveOrUpdateAIHistory({
            aiName: aiName,
            historyId: historyId,
            taskId: promptId,
            aiDesc: aiDesc,
            state: 2,
            aiResultUrl: resultUrl,
            image_large_url: originalUrl,
            aiMediaType: 'video',
            createDate: createDate
          }).catch(function() {})
        })
      }).catch(function(err) {
        self.onProcessFailed(err, historyId, promptId, aiName, aiDesc, originalUrl, createDate)
      })
    },
    onProcessFailed(err, historyId, promptId, aiName, aiDesc, originalUrl, createDate) {
      this.processing = false
      this.processed = false
      this.resultSrc = ''
      this.processError = this.$userFacingError(err, this.$t('common.generateFailedContactSupport'))
      saveOrUpdateAIHistory({
        aiName: aiName,
        historyId: historyId,
        taskId: promptId || '',
        aiDesc: aiDesc,
        state: 3,
        message: (err && err.message) ? err.message : '',
        aiResultUrl: '',
        image_large_url: originalUrl || '',
        aiMediaType: 'video',
        createDate: createDate
      }).catch(function() {})
    }
  }
}
</script>

<style scoped>
@import '../styles/toolPolloLayout.css';

@import './ModelDetailStyles.css';

.tool-detail-page { padding: 48px 0 80px; }
.breadcrumb { display: flex; align-items: center; gap: 8px; margin-bottom: 32px; font-size: 13px; color: var(--text-muted); }
.breadcrumb a { color: var(--text-secondary); }
.breadcrumb a:hover { color: var(--accent); }
.tool-hero { margin-bottom: 32px; }
.tool-hero-info { text-align: center; max-width: 640px; margin: 0 auto; }
.tool-icon-lg { font-size: 48px; display: block; margin-bottom: 16px; }
.tool-hero-info h1 { font-size: 36px; font-weight: 800; margin-bottom: 12px; }
.tool-hero-desc { font-size: 16px; color: var(--text-secondary); line-height: 1.7; margin-bottom: 24px; }
.video-demo-section { margin: 32px auto 0; max-width: 760px; }
.demo-title { font-size: 16px; font-weight: 600; text-align: center; margin-bottom: 20px; color: var(--text-primary); }
.video-compare {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: #111827;
  border: 1px solid var(--bg-card-border);
  box-shadow: var(--shadow-md);
}
.video-compare-media {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #111827;
}
.video-compare-before {
  position: absolute;
  inset: 0;
  z-index: 2;
  overflow: hidden;
  pointer-events: none;
}
.video-compare-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--compare-position);
  z-index: 4;
  transform: translateX(-50%);
  pointer-events: none;
}
.video-compare-handle-line {
  display: block;
  width: 2px;
  height: 100%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.18);
}
.video-compare-handle-knob {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 38px;
  height: 38px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
}
.video-compare-handle-knob span {
  width: 7px;
  height: 7px;
  border-top: 2px solid #111827;
  border-left: 2px solid #111827;
}
.video-compare-handle-knob span:first-child { transform: rotate(-45deg); }
.video-compare-handle-knob span:last-child { transform: rotate(135deg); }
.video-compare-label {
  position: absolute;
  top: 12px;
  z-index: 5;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(17, 24, 39, 0.72);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.video-compare-label-before { left: 12px; }
.video-compare-label-after { right: 12px; }
.video-compare-range {
  position: absolute;
  inset: 0;
  z-index: 6;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: ew-resize;
}
.login-prompt { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 24px 28px; text-align: center; margin: 32px auto 0; max-width: 680px; }
.login-prompt-content { max-width: 360px; margin: 0 auto; }
.login-prompt-content svg { display: block; margin: 0 auto; }
.login-prompt h2 { margin: 6px 0 4px; font-size: 20px; }
.login-prompt p { color: var(--text-muted); font-size: 14px; margin-bottom: 12px; }
.tool-interface { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 32px; margin: 32px auto 0; max-width: 760px; }
.tool-upload { cursor: pointer; }
.upload-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px; border: 2px dashed var(--border-color); border-radius: var(--radius-lg); transition: border-color 0.2s; }
.upload-placeholder:hover { border-color: var(--accent); }
.upload-placeholder p { margin-top: 12px; font-size: 14px; color: var(--text-secondary); }
.upload-placeholder span { font-size: 12px; color: var(--text-muted); }
.upload-preview { display: flex; flex-direction: column; align-items: center; gap: 16px; max-height: none; }
.preview-box { width: 520px; max-width: 100%; aspect-ratio: 16 / 9; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; overflow: hidden; background: #111827; }
.preview-video { width: 100%; height: 100%; object-fit: contain; display: block; background: #111827; }
.tool-error { color: #f87171; font-size: 13px; margin: 12px 0 0; }
.tool-error--block { text-align: center; }
.tool-options { display: flex; align-items: flex-end; gap: 16px; margin-top: 16px; justify-content: center; }
.tool-result { margin-top: 32px; }
.gen-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; gap: 12px; }
.loading-spinner { width: 40px; height: 40px; border: 3px solid var(--border-color); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.output-comparison { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.output-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; text-align: center; }
.output-video-frame { aspect-ratio: 16 / 9; border-radius: var(--radius-lg); overflow: hidden; background: #111827; }
.compare-video { width: 100%; height: 100%; object-fit: contain; display: block; background: #111827; }
.output-actions { display: flex; gap: 12px; justify-content: center; margin-top: 12px; }
.detail-section { margin-bottom: 64px; }
.detail-section h2 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
.steps { display: flex; justify-content: center; gap: 48px; flex-wrap: wrap; }
.step { text-align: center; }
.step-number { width: 40px; height: 40px; border-radius: 50%; background: var(--accent); color: #000; display: flex; align-items: center; justify-content: center; font-weight: 700; margin: 0 auto 12px; }
.step h3 { margin-bottom: 6px; }
.step p { font-size: 13px; color: var(--text-muted); }
.spinner { animation: spin 1s linear infinite; }
@media (max-width: 768px) {
  .tool-interface { padding: 20px; }
  .upload-placeholder { padding: 48px 20px; }
  .output-comparison { grid-template-columns: 1fr; }
  .steps { grid-template-columns: repeat(2, 1fr); }
}
</style>
